import { createBrowserClient } from '@supabase/ssr';

const STORAGE_KEY_PREFIX = 'sb-';

function getProjectRef(): string {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL!;
  return url.match(/https:\/\/([^.]+)\./)?.[1] ?? '';
}

// Read all supabase-related cookies
function getCookies(): { name: string; value: string }[] {
  if (typeof document === 'undefined') return [];
  return document.cookie
    .split(';')
    .filter(Boolean)
    .map((c) => {
      const trimmed = c.trim();
      const eqIdx = trimmed.indexOf('=');
      const name = eqIdx >= 0 ? trimmed.slice(0, eqIdx).trim() : trimmed;
      const value = eqIdx >= 0 ? decodeURIComponent(trimmed.slice(eqIdx + 1)) : '';
      return { name, value };
    })
    .filter((c) => c.name);
}

// Read all supabase-related items from localStorage
function getStorageItems(): { name: string; value: string }[] {
  if (typeof window === 'undefined') return [];
  try {
    const projectRef = getProjectRef();
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(`${STORAGE_KEY_PREFIX}${projectRef}`))
      .map((k) => ({
        name: k,
        value: localStorage.getItem(k) || '',
      }));
  } catch {
    return [];
  }
}

// Write a cookie with Lax fallback (works in iframes served same-origin)
function writeCookie(name: string, value: string, options?: any) {
  if (typeof document === 'undefined') return;
  const maxAge = options?.maxAge ? `; Max-Age=${options.maxAge}` : '';
  const expires = options?.expires ? `; Expires=${new Date(options.expires).toUTCString()}` : '';
  const path = `; Path=${options?.path || '/'}`;
  // Try SameSite=None first (for cross-origin iframes), fallback handled by browser
  document.cookie = `${name}=${encodeURIComponent(value)}${path}; SameSite=None; Secure${maxAge}${expires}`;
  // Also write with Lax for same-origin contexts
  document.cookie = `${name}=${encodeURIComponent(value)}${path}; SameSite=Lax${maxAge}${expires}`;
}

function deleteCookie(name: string) {
  if (typeof document === 'undefined') return;
  const attrs = [
    'Path=/; SameSite=Lax',
    'Path=/; SameSite=None; Secure',
  ];
  attrs.forEach((a) => {
    document.cookie = `${name}=; Max-Age=0; ${a}`;
  });
}

export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll() {
          // Merge cookies + localStorage so we always find the token
          const cookieItems = getCookies();
          const storageItems = getStorageItems().map((item) => ({
            name: item.name.replace(new RegExp(`^${STORAGE_KEY_PREFIX}`), ''),
            value: item.value,
          }));
          // Cookies take priority; supplement with localStorage
          const names = new Set(cookieItems.map((c) => c.name));
          const merged = [...cookieItems];
          storageItems.forEach((s) => {
            if (!names.has(s.name)) merged.push(s);
          });
          return merged;
        },
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          cookiesToSet.forEach(({ name, value, options }) => {
            if (value) {
              // Always write to both cookie and localStorage for resilience
              writeCookie(name, value, options);
              try {
                localStorage.setItem(`${STORAGE_KEY_PREFIX}${name}`, value);
              } catch {}
            } else {
              deleteCookie(name);
              try {
                localStorage.removeItem(`${STORAGE_KEY_PREFIX}${name}`);
              } catch {}
            }
          });
        },
      },
    }
  );
}
